import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { generateCustomerReport } from "../utils/pdfGenerator";

// ─── Admin Pet Edit Panel ─────────────────────────────────────────────────────
const AdminUserPetPanel = ({ userObj, pet, petIndex, userToken, onSaved, onShowQr }) => {
  const isNew = !pet._id;
  const [editing, setEditing] = useState(isNew);
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState(null);
  
  const [form, setForm] = useState({
    petName:       pet.petName       || "",
    petType:       pet.petType       || "dog",
    breed:         pet.breed         || "",
    birthday:      pet.birthday ? new Date(pet.birthday).toISOString().split("T")[0] : "",
    gender:        pet.gender        || "unknown",
    colorMarkings: pet.colorMarkings || "",
    behaviorNotes: pet.behaviorNotes || "",
    photoUrl:      pet.photoUrl      || "",
    health: {
      isVaccinated:        pet.health?.isVaccinated        ?? false,
      lastVaccinationDate: pet.health?.lastVaccinationDate
        ? new Date(pet.health.lastVaccinationDate).toISOString().split("T")[0]
        : "",
      medicalConditions:   pet.health?.medicalConditions   || "",
      medication:          pet.health?.medication          || "",
    },
  });

  const set  = (f, v) => setForm((p) => ({ ...p, [f]: v }));
  const setH = (f, v) => setForm((p) => ({ ...p, health: { ...p.health, [f]: v } }));

  const compressImage = (file, maxPx = 500, quality = 0.72) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const ratio  = Math.min(maxPx / img.width, maxPx / img.height, 1);
          const canvas = document.createElement("canvas");
          canvas.width  = img.width  * ratio;
          canvas.height = img.height * ratio;
          canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL("image/jpeg", quality));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });

  const handleSave = async () => {
    if (!form.petName.trim() || !form.petType) {
      setErr("Name and Type are required.");
      return;
    }
    if (!form.birthday) {
      setErr("Birthdate is required.");
      return;
    }
    setSaving(true); setErr(null);
    try {
      if (isNew) {
        await axios.post(
          `/api/auth/users/${userObj._id}/pets`,
          { ...form },
          { headers: { Authorization: `Bearer ${userToken}` } }
        );
      } else {
        await axios.put(
          `/api/auth/users/${userObj._id}/pets/${pet._id}`,
          { ...form },
          { headers: { Authorization: `Bearer ${userToken}` } }
        );
      }
      setEditing(false);
      if (onSaved) onSaved();
    } catch (e) {
      setErr(e.response?.data?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (isNew) {
      if (onSaved) onSaved(); // Just cancel adding new pet
      return;
    }
    if (!window.confirm("Are you sure you want to remove this pet profile?")) return;
    setSaving(true);
    try {
      await axios.delete(
        `/api/auth/users/${userObj._id}/pets/${pet._id}`,
        { headers: { Authorization: `Bearer ${userToken}` } }
      );
      if (onSaved) onSaved();
    } catch (e) {
      setErr("Delete failed.");
      setSaving(false);
    }
  };

  const inputCls = "w-full px-3 py-2 rounded-xl border-2 border-purple-100 bg-white text-gray-800 text-sm font-medium focus:outline-none focus:border-purple-400 transition-all";

  return (
    <div className="border-2 border-purple-50 rounded-2xl overflow-hidden mb-3">
      {/* Pet header */}
      {!editing ? (
        <div className="flex items-center justify-between px-4 py-3 bg-purple-50/40">
          <div className="flex items-center gap-3">
            {pet.photoUrl
              ? <img src={pet.photoUrl} alt={pet.petName} className="w-10 h-10 rounded-xl object-cover border-2 border-purple-200" />
              : <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-300 to-pink-300 flex items-center justify-center text-xl">{pet.petType === "cat" ? "🐱" : "🐶"}</div>
            }
            <div>
              <p className="font-black text-gray-800 text-sm">{pet.petName}</p>
              <p className="text-xs text-gray-500 capitalize">{pet.petType} · {pet.breed || "Mixed"} · {pet.gender === "male" ? "♂️" : pet.gender === "female" ? "♀️" : "❓"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {pet._id && (
              <button
                onClick={() => onShowQr && onShowQr(userObj._id, pet._id, pet.petName)}
                title="Show QR Code"
                className="px-3 py-1.5 rounded-xl text-xs font-bold bg-purple-50 text-purple-600 border-2 border-purple-200 hover:bg-purple-100 transition-all"
              >
                📱 QR
              </button>
            )}
            <button
              onClick={() => { setEditing(true); setErr(null); }}
              className="px-3 py-1.5 rounded-xl text-xs font-bold bg-white text-purple-600 border-2 border-purple-200 hover:bg-purple-50 transition-all"
            >
              ✏️ Edit
            </button>
          </div>
        </div>
      ) : (
        <div className="p-4 space-y-4 bg-white">
          <div className="flex justify-between items-center pb-2 border-b border-purple-50">
            <h4 className="font-black text-purple-700">{isNew ? "New Pet Profile" : "Edit Pet Profile"}</h4>
            {!isNew && (
              <button onClick={handleDelete} disabled={saving} className="text-red-500 hover:text-red-700 text-xs font-bold transition-colors">
                🗑️ Remove
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1">Pet Name *</label>
              <input className={inputCls} value={form.petName} onChange={(e) => set("petName", e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1">Type *</label>
              <select className={inputCls} value={form.petType} onChange={(e) => set("petType", e.target.value)}>
                <option value="dog">Dog 🐶</option>
                <option value="cat">Cat 🐱</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1">Breed</label>
              <input className={inputCls} value={form.breed} onChange={(e) => set("breed", e.target.value)} placeholder="e.g. Shih Tzu" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1">Gender</label>
              <select className={inputCls} value={form.gender} onChange={(e) => set("gender", e.target.value)}>
                <option value="male">Male ♂️</option>
                <option value="female">Female ♀️</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1">🎂 Birthdate *</label>
              <input type="date" className={inputCls} value={form.birthday} max={new Date().toISOString().split("T")[0]} onChange={(e) => set("birthday", e.target.value)} required />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1">🎨 Color / Markings</label>
              <input className={inputCls} value={form.colorMarkings} onChange={(e) => set("colorMarkings", e.target.value)} />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1">Behavior Notes</label>
            <textarea className={`${inputCls} resize-none`} rows={2} value={form.behaviorNotes} onChange={(e) => set("behaviorNotes", e.target.value)} />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 mb-2">📸 Pet Photo</label>
            <div className="flex items-start gap-3">
              {form.photoUrl && (
                <div className="relative flex-shrink-0">
                  <img src={form.photoUrl} alt="preview" className="w-16 h-16 object-cover rounded-xl border-2 border-purple-100" />
                  <button type="button" onClick={() => set("photoUrl", "")} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs font-black flex items-center justify-center">×</button>
                </div>
              )}
              <label className="flex-1 flex flex-col items-center justify-center border-2 border-dashed border-purple-200 rounded-xl p-3 cursor-pointer hover:border-purple-400 transition-all">
                <span className="text-xl mb-1">📷</span>
                <span className="text-xs font-bold text-purple-500">{form.photoUrl ? "Change photo" : "Upload photo"}</span>
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  set("photoUrl", await compressImage(file));
                }} />
              </label>
            </div>
          </div>

          <div className="bg-purple-50/60 rounded-2xl p-4 space-y-3">
            <p className="text-xs font-black uppercase tracking-widest text-purple-500">Health Info</p>
            <div className="flex items-center gap-2">
              <input type="checkbox" id={`adm-vax-${userObj._id}-${petIndex}`} checked={form.health.isVaccinated} onChange={(e) => setH("isVaccinated", e.target.checked)} className="w-4 h-4 accent-purple-600" />
              <label htmlFor={`adm-vax-${userObj._id}-${petIndex}`} className="text-sm font-bold text-gray-700 cursor-pointer">Vaccinated</label>
            </div>
            {form.health.isVaccinated && (
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1">Last Vaccination Date</label>
                <input type="date" className={inputCls} value={form.health.lastVaccinationDate} onChange={(e) => setH("lastVaccinationDate", e.target.value)} />
              </div>
            )}
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1">Medical Conditions</label>
              <input className={inputCls} value={form.health.medicalConditions} onChange={(e) => setH("medicalConditions", e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1">Medication</label>
              <input className={inputCls} value={form.health.medication} onChange={(e) => setH("medication", e.target.value)} />
            </div>
          </div>

          {err && <p className="text-xs text-red-600 font-bold bg-red-50 px-3 py-2 rounded-xl">⚠️ {err}</p>}

          <div className="flex gap-3">
            <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl text-sm hover:scale-[1.01] transition-all disabled:opacity-60">
              {saving ? "Saving…" : "💾 Save"}
            </button>
            <button onClick={() => { 
                if (isNew) { if (onSaved) onSaved(); } 
                else { setEditing(false); setErr(null); }
              }} disabled={saving} className="px-5 py-2.5 bg-white border-2 border-gray-200 text-gray-600 font-bold rounded-xl text-sm">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};


// ─── Admin Users Main Component ───────────────────────────────────────────────
const AdminUsers = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [addingPetToUser, setAddingPetToUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [qrModal, setQrModal] = useState(null);
  const [createStaffModal, setCreateStaffModal] = useState(false);
  const [staffForm, setStaffForm] = useState({ name: "", email: "", role: "staff" });
  const [staffFormErr, setStaffFormErr] = useState("");
  const [staffFormLoading, setStaffFormLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get("/api/auth/users", {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      if (res.data.success) setUsers(res.data.data);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching users:", err);
      setLoading(false);
    }
  }, [user.token]);

  useEffect(() => { 
    fetchUsers(); 
  }, [fetchUsers]);

  const deleteUser = async (id) => {
    if (!window.confirm("Are you sure you want to permanently delete this user? This cannot be undone.")) return;
    try {
      await axios.delete(`/api/auth/users/${id}`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete user");
    }
  };

  const handleCreateStaff = async (e) => {
    e.preventDefault();
    if (!staffForm.name.trim() || !staffForm.email.trim()) {
      setStaffFormErr("Name and email are required."); return;
    }
    setStaffFormLoading(true); setStaffFormErr("");
    try {
      await axios.post("/api/auth/staff", staffForm, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setCreateStaffModal(false);
      setStaffForm({ name: "", email: "", role: "staff" });
      fetchUsers();
    } catch (err) {
      setStaffFormErr(err.response?.data?.message || "Failed to create staff account.");
    } finally {
      setStaffFormLoading(false);
    }
  };


  if (loading) return <div className="text-center py-20 text-purple-600 font-bold">Loading users...</div>;

  const filteredUsers = users.filter(u => {
    const term = searchTerm.toLowerCase();
    return (u.name || "").toLowerCase().includes(term) ||
           (u.email || "").toLowerCase().includes(term) ||
           (u.phone || "").toLowerCase().includes(term);
  });

  return (
    <>
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h2 className="text-2xl font-black text-gray-800">User Management</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={() => { setCreateStaffModal(true); setStaffFormErr(""); }}
            className="px-4 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-600 text-white text-xs font-black rounded-xl shadow-sm hover:scale-[1.02] transition-all"
          >
            + Create Admin / Staff Account
          </button>
          <div className="relative w-full sm:w-64">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">🔍</span>
            <input
              type="text"
              className="w-full pl-10 pr-4 py-2 border-2 border-purple-100 rounded-xl bg-white text-sm focus:outline-none focus:border-purple-400 transition-all"
              placeholder="Search name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={() => generateCustomerReport(filteredUsers)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 font-bold rounded-xl text-xs hover:bg-purple-200 transition-all border border-purple-200"
          >
           PDF Report
          </button>
        </div>
      </div>
      
      <div className="space-y-4">
        {filteredUsers.length === 0 ? (
          <div className="text-center py-10 text-gray-500 font-bold">No users found matching "{searchTerm}"</div>
        ) : (
          filteredUsers.map(u => {
          const isOpen = expanded === u._id;
          
          return (
            <div key={u._id} className="bg-white/80 backdrop-blur rounded-3xl border-2 border-purple-50 shadow-sm overflow-hidden">
              {/* Summary row */}
              <div className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap mb-1">
                    <p className="font-black text-gray-800">{u.name}</p>
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${
                      u.isActive ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-600"
                    }`}>
                      {u.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">{u.email} · {u.phone || "No phone"}</p>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  {u.role !== "admin" && (
                    <button
                      onClick={() => deleteUser(u._id)}
                      className="px-3 py-1.5 rounded-lg text-xs font-bold bg-red-50 text-red-500 hover:bg-red-500 hover:text-white transition-all border border-red-100"
                    >
                      🗑️ Delete
                    </button>
                  )}
                  <button
                    onClick={() => setExpanded(isOpen ? null : u._id)}
                    className="flex items-center gap-1 px-3 py-1.5 border-2 border-purple-100 text-purple-600 rounded-xl text-xs font-bold hover:bg-purple-50 transition-all"
                  >
                    🐾 Profiles ({u.pets?.length || 0})
                    <svg className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Expanded pet profiles panel */}
              {isOpen && (
                <div className="px-5 pb-5 space-y-3 border-t border-purple-50 pt-4 bg-purple-50/20">
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-xs font-black uppercase tracking-widest text-purple-400">Persistent Pet Profiles</p>
                    <button 
                      onClick={() => setAddingPetToUser(u._id)}
                      className="text-xs font-bold text-purple-600 hover:text-purple-800 transition-colors"
                    >
                      + Add New Pet
                    </button>
                  </div>
                  
                  {addingPetToUser === u._id && (
                    <AdminUserPetPanel
                      userObj={u}
                      pet={{}} // Empty pet triggers "new" mode
                      petIndex={-1}
                      userToken={user.token}
                      onSaved={() => { setAddingPetToUser(null); fetchUsers(); }}
                      onShowQr={null} // no QR for unsaved pets
                    />
                  )}

                  {(!u.pets || u.pets.length === 0) && addingPetToUser !== u._id && (
                    <p className="text-sm text-gray-500 italic py-2">No pets registered for this user.</p>
                  )}

                  {(u.pets || []).map((pet, idx) => (
                    <AdminUserPetPanel
                      key={pet?._id || (typeof pet === "string" ? pet : idx)}
                      userObj={u}
                      pet={pet}
                      petIndex={idx}
                      userToken={user.token}
                      onSaved={() => fetchUsers()}
                      onShowQr={(userId, petId, petName) => setQrModal({ userId, petId, petName })}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        }))}
      </div>
    </div>

    {/* QR Code Modal */}
    {qrModal && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
        onClick={(e) => { if (e.target === e.currentTarget) setQrModal(null); }}
      >
        <div className="bg-white rounded-3xl shadow-2xl p-8 flex flex-col items-center gap-5 max-w-xs w-full">
          <div className="text-center">
            <p className="text-xs font-black uppercase tracking-widest text-purple-400 mb-1">Pet QR Code</p>
            <h3 className="text-xl font-black text-gray-800">{qrModal.petName}</h3>
          </div>

          <div className="p-4 bg-white rounded-2xl border-4 border-purple-100 shadow-inner">
            <QRCodeCanvas
              id="pet-qr-canvas"
              value={`${window.location.origin}/pet/${qrModal.userId}/${qrModal.petId}`}
              size={200}
              bgColor="#ffffff"
              fgColor="#000000"
              level="M"
            />
          </div>

          <p className="text-xs text-gray-500 text-center font-medium">
            Scan this code to instantly view<br />{qrModal.petName}'s profile.
          </p>

          <div className="flex gap-3 w-full">
            <button
              onClick={() => {
                const canvas = document.getElementById("pet-qr-canvas");
                const url = canvas.toDataURL("image/png");
                const a = document.createElement("a");
                a.href = url;
                a.download = `${qrModal.petName}-qr.png`;
                a.click();
              }}
              className="flex-1 py-2.5 bg-purple-600 text-white font-bold rounded-xl text-sm hover:bg-purple-700 transition-all"
            >
              ⬇️ Download
            </button>
            <button
              onClick={() => setQrModal(null)}
              className="flex-1 py-2.5 bg-gray-100 text-gray-600 font-bold rounded-xl text-sm hover:bg-gray-200 transition-all"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ── Create Staff Modal ── */}
    {createStaffModal && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        onClick={(e) => { if (e.target === e.currentTarget) setCreateStaffModal(false); }}
      >
        <div className="bg-slate-900 rounded-3xl shadow-2xl p-8 w-full max-w-md border border-slate-700">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl mx-auto mb-4 flex items-center justify-center text-3xl">👷</div>
            <h3 className="text-xl font-black text-white">Create Internal Account</h3>
            <p className="text-slate-400 text-xs mt-1">Users will be prompted to set their own name and password on first login.</p>
          </div>

          {staffFormErr && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-xs font-bold">⚠️ {staffFormErr}</div>
          )}

          <form onSubmit={handleCreateStaff} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Display Name</label>
              <input
                type="text"
                value={staffForm.name}
                onChange={e => { setStaffForm(f => ({ ...f, name: e.target.value })); setStaffFormErr(""); }}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white text-sm font-medium focus:outline-none focus:border-teal-500 transition-all placeholder-slate-500"
                placeholder="e.g. Staff Member"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Login Email</label>
              <input
                type="email"
                value={staffForm.email}
                onChange={e => { setStaffForm(f => ({ ...f, email: e.target.value })); setStaffFormErr(""); }}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white text-sm font-medium focus:outline-none focus:border-teal-500 transition-all placeholder-slate-500"
                placeholder="staff@furpecttails.com"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Role</label>
              <select
                value={staffForm.role}
                onChange={e => setStaffForm(f => ({ ...f, role: e.target.value }))}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white text-sm font-medium focus:outline-none focus:border-teal-500 transition-all"
              >
                <option value="staff">Staff</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={staffFormLoading}
                className="flex-1 py-3 bg-gradient-to-r from-teal-500 to-emerald-600 text-white font-black rounded-xl text-sm hover:scale-[1.02] transition-all disabled:opacity-60"
              >
                {staffFormLoading ? "Creating…" : "Create Account"}
              </button>
              <button
                type="button"
                onClick={() => setCreateStaffModal(false)}
                className="flex-1 py-3 bg-slate-800 text-slate-400 font-bold rounded-xl text-sm border border-slate-700 hover:bg-slate-700 transition-all"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
    </>
  );
};

export default AdminUsers;
